import { SchedulingRule, SchedulingRuleResponse, SchedulingRuleBase, SchedulingRuleRequest } from './@types/SchedulingRuleModel';

type SchedulingRuleApiResponse = SchedulingRuleResponse | SchedulingRule[];

const normalizeSchedulingRule = (rule: SchedulingRule) => {
    const id = rule.ruleset_id ?? rule.scheduling_rule_id ?? rule.id;

    return {
        ...rule,
        id,
        ruleset_id: id,
        scheduling_rule_id: rule.scheduling_rule_id ?? id,
    };
};

const unwrapRules = (response: SchedulingRuleApiResponse): SchedulingRule[] => {
    if (Array.isArray(response)) {
        return response;
    }

    return response.data ?? [];
};

export class SchedulingRulesRepository {
    private httpService: IHttpService;
    private baseUrl: string;

    constructor(httpService: IHttpService) {
        this.httpService = httpService;
        this.baseUrl = '/ruleset';
    }

    async getAll(): Promise<SchedulingRule[]> {
        const fallbackUrls = [this.baseUrl, '/rulesets', '/scheduling_rules'];
        let lastError: unknown;

        for (const url of fallbackUrls) {
            try {
                const response = await this.httpService.get<SchedulingRuleApiResponse>(url);
                const data = unwrapRules(response).map(normalizeSchedulingRule);
                return data ?? [];
            } catch (error) {
                lastError = error;
            }
        }

        throw lastError;
    }

    async create(rule: SchedulingRuleBase): Promise<SchedulingRule> {
        const response = await this.httpService.post<SchedulingRuleRequest, SchedulingRuleBase>(this.baseUrl, { data: rule });
        if (!response?.data) {
            throw new Error('Invalid response creating scheduling rule');
        }

        return normalizeSchedulingRule(response.data);
    }

    async update(id: number, rule: SchedulingRuleBase): Promise<SchedulingRule> {
        const response = await this.httpService.patch<SchedulingRuleRequest, SchedulingRuleBase>(`${this.baseUrl}/${id}`, { data: rule });
        if (!response?.data) {
            throw new Error('Invalid response updating scheduling rule');
        }

        return normalizeSchedulingRule(response.data);
    }

    async delete(id: number): Promise<void> {
        await this.httpService.delete<void>(`${this.baseUrl}/${id}`);
    }
}
